import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxUILinkComponent } from '@flowx/angular-ui-toolkit'
import { DecorativeAccessibilityProps, LinkTemplateConfig, localizeString } from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { getBoolExpressionSignal, getComputedValueSignal, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-link',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [FlxUILinkComponent, FlxLocalizePipe, FlxThemeModule, FlxStyleModule],
  templateUrl: './flx-link.component.html',
  styleUrl: './flx-link.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxLinkComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  #sanitizer = inject(DomSanitizer)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<LinkTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)
  accessibility = computed(
    (): DecorativeAccessibilityProps | null => this.flowxProps()?.accessibility
  )

  evaluatedConditionalStyles = signal([])
  conditionalStyles = computed(() =>
    this.evaluatedConditionalStyles()
      .filter(({ expression }) => expression())
      .map(({ overrides }) => overrides)
  )

  // expressions
  isHidden: Signal<boolean>
  ariaHidden: Signal<boolean>

  // dynamic props
  text: Signal<string>
  url: Signal<SafeUrl>

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const { text, isComputedText, hide, url } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.ariaHidden = computed(() => this.accessibility()?.decorative)
      this.text = isComputedText
        ? getComputedValueSignal(text, this.templateConfig())
        : getDynamicPropSignal(text, this.templateConfig())

      const rawUrl = getDynamicPropSignal(url, this.templateConfig(), false, true)
      this.url = computed(() => this.#sanitizer.bypassSecurityTrustUrl(localizeString(rawUrl())))

      const { conditionals } = this.style()
      if (Array.isArray(conditionals)) {
        this.evaluatedConditionalStyles.set(
          conditionals.map(({ condition, overrides }, index) => ({
            expression: getBoolExpressionSignal(condition, this.templateConfig(), {
              logType: 'conditional',
              conditionIndex: index,
            }),
            overrides,
          }))
        )
      }
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
